import {useState, useEffect, useMemo} from 'react';
import styles from '../Universidades/Formulario.module.css'; 
import Swal from 'sweetalert2'; 
import {useAuth} from '../../Auth/AuthContext'; 
import PhoneInput from 'react-phone-input-2';   
import 'react-phone-input-2/lib/style.css';

const AniadirUsuario = ({ abrirModal, cerrarModal, onUsuarioCreado }) => { 
    const { getToken } = useAuth(); 
    const [formData, setFormData] = useState({ 
        nombre: '', 
        appa: '', 
        apma: '', 
        correo: '', 
        curp: '', 
        fecha_nacimiento: '', 
        contrasenia: '', 
        confcontrasenia: '', 
        telefono: '', 
        correo_tutor: '',
        tipo_usuario: 1
    }); 

    const [errores, setErrores] = useState({}); 
    const [isClosing, setIsClosing] = useState(false); 

    //Tipo de usuarios. 
    const tiposUsuarios = [ 
        { value: 1, label: 'Estudiante'}, 
        { value: 2, label: 'Administrador'}, 
        { value: 3, label: 'Tutor'}, 
    ]; 

    //Validaciones. 
    const validators = useMemo(() => ({ 
        email: (email) => { 
            if (!email) return 'El correo es requerido.'; 
            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            return !emailRegex.test(email) ? 'El correo no es válido.' : ''; 
        }, 

        contrasenia: (contrasenia, confirmacion = '') => { 
            if (!contrasenia) return 'La contraseña es requerida.'; 
            if (confirmacion && contrasenia !== confirmacion) return 'Las contraseñas no coinciden.'; 
            return ''; 
        }, 

        curp: (curp) => { 
            if (!curp) return 'La CURP es requerida. '; 
            const curpRegex = /^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9A-Z]{2}$/;
            return !curpRegex.test(curp) ? 'La CURP no es válida.' : '';
        },

        telefono: (telefono) => { 
            if (!telefono) return 'El teléfono es requerido.'; 
            const numeroLimpio = telefono.replace(/\D/g, ''); 
            if (numeroLimpio.length < 10) return 'El teléfono debe tener al menos 10 dígitos.'; 
            if (numeroLimpio.length > 14) return 'El teléfono no debe exceder 14 dígitos.'; 
            return ''; 
        }, 

        requerido: (valor, campo) => { 
            return !valor ? `${campo} es requerido.` : '';
        }
    }), []); 

    const handleCloseModal = () => { 
        setIsClosing(true); 
    }; 

    const token = getToken(); 

    useEffect(() => { 
        if (isClosing) { 
            const timer = setTimeout(() => {
                cerrarModal(); 
                setIsClosing(false); 
            }, 300); 
            return () => clearTimeout(timer); 
        }
    }, [isClosing, cerrarModal]); 

    const handleInputChange = (field, value) => { 
        setFormData(prev => ({
            ...prev, 
            [field] : value
        })); 

        // Validación en tiempo real
        const shouldValidate = field === 'confcontrasenia' || field === 'tipo_usuario' || 
                                (typeof value === 'string' && value.trim() !== '') ||
                                (typeof value === 'number') ||
                                (value && typeof value !== 'string');

        if (shouldValidate) {
            const error = validarCampo(
                field, 
                value, 
                field === 'confcontrasenia' ? formData.contrasenia : 
                field === 'contrasenia' ? formData.confcontrasenia : ''
            );
            
            setErrores(prev => ({
                ...prev,
                [field]: error
            }));
        } else {
            // Limpiar error si el campo se vacía
            setErrores(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    }; 

    const validarCampo = (campo, valor, valorExtra = '') => { 
        switch (campo) {
            case 'correo':
                return validators.email(valor);
            case 'correo_tutor':
                return validators.email(valor);
            case 'contrasenia':
                return validators.contrasenia(valor, valorExtra);
            case 'confcontrasenia':
                return validators.contrasenia(formData.contrasenia, valor);
            case 'curp':
                return validators.curp(valor);
            case 'telefono':
                return validators.telefono(valor);
            case 'nombre':
                return validators.requerido(valor, 'El nombre');
            case 'appa':
                return validators.requerido(valor, 'El apellido paterno');
            case 'apma':
                return validators.requerido(valor, 'El apellido materno');
            case 'fecha_nacimiento':
                return validators.requerido(valor, 'La fecha de nacimiento');
            default:
                return '';
    }
}; 

const validarFormulario = () => { 
    const nuevosErrores = {}; 
    const {tipo_usuario} = formData; 
    const esEstudiante = tipo_usuario === 1; 

    nuevosErrores.nombre = validarCampo('nombre', formData.nombre); 
    nuevosErrores.appa = validarCampo('appa', formData.appa); 
    nuevosErrores.apma = validarCampo ('apma', formData.apma); 
    nuevosErrores.correo = validarCampo('correo', formData.correo); 
    nuevosErrores.contrasenia = validarCampo('contrasenia', formData.contrasenia); 
    nuevosErrores.confcontrasenia = validarCampo('confcontrasenia', formData.confcontrasenia);

    if (esEstudiante) {
        nuevosErrores.curp = validarCampo('curp', formData.curp); 
        nuevosErrores.fecha_nacimiento = validarCampo('fecha_nacimiento', formData.fecha_nacimiento); 
        nuevosErrores.telefono = validarCampo('telefono', formData.telefono);
        nuevosErrores.correo_tutor = validarCampo('correo_tutor', formData.correo_tutor); 
    }

    const erroresFiltrados = Object.fromEntries(
        Object.entries(nuevosErrores).filter(([_, value]) => value !== '')
    ); 

    setErrores(erroresFiltrados); 
    return Object.keys(erroresFiltrados).length === 0; 
}; 

const handleOverlayClick = (e) => { 
    if (e.target === e.currentTarget) { 
        handleCloseModal(); 
    }
}; 

const handleSubmit = async (e) => { 
    e.preventDefault(); 
    if (!validarFormulario()) return; 
    
    const esAdmin = formData.tipo_usuario === 2;

    const datosEnvio = { 
        nombre: formData.nombre, 
        appa: formData.appa, 
        apma: formData.apma, 
        correo: formData.correo, 
        contrasenia: formData.contrasenia, 
        curp: esAdmin ? '-' : formData.curp, 
        fecha_nacimiento: esAdmin ? '1900-01-01' : formData.fecha_nacimiento, 
        estado: '-', 
        municipio: '-', 
        bachillerato: '-', 
        matricula: '-', 
        institucion: '-', 
        telefono: esAdmin ? '-' : formData.telefono, 
        correo_tutor: esAdmin ? '-' : formData.correo_tutor, 
        tipo_usuario: formData.tipo_usuario
    }; 

    try { 
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/registro/admin`, { 
            method: 'POST', 
            headers: { 
                'Authorization': `Bearer ${token}`, 
                'Content-Type': 'application/json', 
            }, 
            body: JSON.stringify(datosEnvio), 
        }); 

        if (!response.ok) { 
            const data = await response.json(); 
            throw new Error (data.error || 'Error al registrar'); 
        }

        await Swal.fire({ 
            title: 'Usuario creado correctamente.', 
            icon: 'success', 
            confirmButtonText: 'Continuar', 
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito', 
            }, 
        }); 
        if (onUsuarioCreado) { 
            await onUsuarioCreado(); 
        }
        cerrarModal(); 
    } catch (error) { 
        await Swal.fire({ 
            title: 'Error', 
            text: error.message || 'Ha ocurrido un error al registrar.', 
            icon: 'error', 
            confirmButtonText: 'Entendido', 
            confirmButtonColor: '#242b57', 
            customClass: {
                popup: 'popupbonito', 
            }, 
        }); 
        if (error.message.includes('correo')) { 
            setErrores(prev => ({ 
                ...prev, 
                correo: error.message
            })); 
        }
    }
}; 

if (!abrirModal && !isClosing) return null; 

const esEstudiante = formData.tipo_usuario === 1; 
const esAdmin = formData.tipo_usuario === 2;
const esTutor = formData.tipo_usuario === 3; 

return ( 
    <div className={styles.modalAniadir} onClick={handleOverlayClick}>
        <div className={`${styles.modalScrollableAniadir} ${
                    abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''
        }`}>
                <button onClick = {handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                    ×
                </button>
                <h2 className={styles.tituloAniadir}>Añadir usuario</h2>
                <form className={styles.formUsuario} onSubmit={handleSubmit}>

                    {/*Tipo de usuario*/}
                    <label htmlFor="tipo_usuario">
                        Tipo de usuario: 
                        <select 
                            id="tipo_usuario"
                            value={formData.tipo_usuario} 
                            onChange={(e) => handleInputChange('tipo_usuario', parseInt(e.target.value))}
                            required
                        >
                            {tiposUsuarios.map(tipo => (
                                <option key={tipo.value} value = {tipo.value}>
                                    {tipo.label} 
                                </option>
                            ))}
                        </select>
                    </label>

                    {/*Campos comunes*/}
                    <label htmlFor="name">
                        Nombre(s): 
                        <input  
                            type="text"
                            id="name"
                            value={formData.nombre}
                            placeholder="Ingresa el nombre"
                            onChange={(e) => handleInputChange('nombre', e.target.value)}
                            required
                        />
                    </label>
                    {errores.nombre && <p className={styles.mensajeErrorAniadir}>{errores.nombre}</p>}

                    <label htmlFor="appa">
                        Apellido paterno:
                        <input
                            type="text"
                            id="appa"
                            value={formData.appa}
                            placeholder="Ingresa el apellido paterno"
                            onChange={(e) => handleInputChange('appa', e.target.value)}
                            required
                        />
                    </label>
                    {errores.appa && <p className={styles.mensajeErrorAniadir}>{errores.appa}</p>}

                    <label htmlFor="apma">
                        Apellido materno:
                        <input
                            type="text"
                            id="apma"
                            value={formData.apma}
                            placeholder="Ingresa el apellido materno"
                            onChange={(e) => handleInputChange('apma', e.target.value)}
                            required
                            />
                    </label>
                    {errores.apma && <p className={styles.mensajeErrorAniadir}>{errores.apma}</p>}

                    <label htmlFor="email">
                        Correo electrónico: 
                        <input 
                            type="email"
                            id="email"
                            value={formData.correo}
                            placeholder="Ingresa el correo"
                            onChange={(e) => handleInputChange('correo', e.target.value)}
                            required
                        /> 
                    </label>
                    {errores.correo && <p className={styles.mensajeErrorAniadir}>{errores.correo}</p>}

                    <label htmlFor="password">
                        Contraseña: 
                        <input 
                            type="password"
                            id="password"
                            value={formData.contrasenia}
                            onChange={(e) => handleInputChange('contrasenia', e.target.value)}
                            placeholder='Ingresa la contraseña'
                            required
                        />
                    </label>

                    <label htmlFor="confirmar">
                            Confirmar contraseña:
                            <input
                                type="password"
                                id="confirmar"
                                value={formData.confcontrasenia}
                                onChange={(e) => handleInputChange('confcontrasenia', e.target.value)}
                                placeholder="Confirma la contraseña"
                                required
                            />
                        </label>
                        {errores.confcontrasenia && <p className={styles.mensajeErrorAniadir}>{errores.confcontrasenia}</p>}
                        {errores.contrasenia && <p className={styles.mensajeErrorAniadir}>{errores.contrasenia}</p>}

                        {esTutor && (
                            <>
                                <label htmlFor="date">
                                    Fecha de nacimiento: 
                                    <input 
                                        type="date"
                                        id="date"
                                        value={formData.fecha_nacimiento}
                                        onChange={(e) => handleInputChange('fecha_nacimiento', e.target.value)}
                                        required
                                />
                            </label>
                            {errores.fecha_nacimiento && <p className={styles.mensajeErrorAniadir}>{errores.fecha_nacimiento}</p>}
                            </>
                        )}

                        {/*Campos para estudiantes.*/}
                        {esEstudiante && (
                            <>
                            <label htmlFor="date">
                                Fecha de nacimiento: 
                                <input 
                                    type="date"
                                    id="date"
                                    value={formData.fecha_nacimiento}
                                    onChange={(e) => handleInputChange('fecha_nacimiento', e.target.value)}
                                    required
                                />
                            </label>
                            {errores.fecha_nacimiento && <p className={styles.mensajeErrorAniadir}>{errores.fecha_nacimiento}</p>}

                            <label htmlFor="curp">
                                CURP: 
                                <span className={styles.helpText}>
                                    Si no conoces la CURP busca{' '}
                                    <a href="https://www.gob.mx/curp/" target="_blank" rel="noopener noreferrer">
                                    AQUÍ
                                    </a>
                                </span>
                                <input 
                                    type="text"
                                    value={formData.curp}
                                    placeholder="Ingresa la CURP"
                                    onChange={(e) => handleInputChange('curp', e.target.value.toUpperCase())}
                                    maxLength={18}
                                    required
                                /> 
                            </label>
                            {errores.curp && <p className={styles.mensajeErrorAniadir}>{errores.curp}</p>}

                            <label htmlFor="telefono">
                                    Teléfono de contacto:
                                    <PhoneInput
                                        country={'mx'}
                                        value={formData.telefono}
                                        onChange={(value) => handleInputChange('telefono', value)}
                                        inputClass={styles.inputTelefono}
                                        containerClass={styles.phoneInputContainer}
                                        placeholder="Ingresa un teléfono"
                                        preferredCountries={['mx', 'us', 'ca']}
                                        enableSearch={true}
                                        searchPlaceholder="Buscar país..."
                                        required
                                    />
                                </label>
                                {errores.telefono && <p className={styles.mensajeErrorAniadir}>{errores.telefono}</p>}

                                <label htmlFor="correo_tutor">
                                    Correo electrónico del tutor:
                                    <input 
                                        type="email"
                                        id="correo_tutor"
                                        value={formData.correo_tutor}
                                        placeholder="Ingresa el correo del tutor"
                                        onChange={(e) => handleInputChange('correo_tutor', e.target.value)}
                                        required
                                    />
                                </label>
                                {errores.correo_tutor && <p className={styles.mensajeErrorAniadir}>{errores.correo_tutor}</p>}
                            </>
                    )}
                    <button type="submit" className={styles.botonGuard}>
                            Crear {esAdmin ? 'administrador' : 'usuario'}
                    </button>
                </form>
            </div>
        </div>
);
};

export default AniadirUsuario;